import React, { useState, useMemo } from "react";
import { Check } from "lucide-react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../dataManagement/uiDb/UiDb";
import { ProductDetailsModal } from "../../../../interfaces/ProductObject";
import {
  selectDineInSession,
  selectDineInRounds,
  selectDineInRoundItems,
  selectDineInMembers,
  selectDineInProducts,
} from "../../../../dataManagement/uiDb/DineInDb";
import { buildDineInFeedRows } from "../dineInFeedBuilder";
import { roundOrderSummary } from "../../../../services/helpers/DineInSummaryHelper";
import DineInFeedItemCard from "./DineInFeedItemCard";
import DineInProductDetailsModal from "./DineInProductDetailsModal";
import "./styles/DineInFeedList.css";

/** What a tapped card hands up — everything DineInProductDetailsModal needs,
 *  already resolved (name/time formatting) exactly as the card displays it. */
interface SelectedFeedItem {
  product?: ProductDetailsModal;
  variantId?: string;
  quantity: number;
  addedByName: string;
  addedAt: string;
}

const formatTime = (iso: string) =>
  iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "";

const DineInFeedList: React.FC = () => {
  const session = useSelector(selectDineInSession);
  const rounds = useSelector(selectDineInRounds);
  const roundItems = useSelector(selectDineInRoundItems);
  const members = useSelector(selectDineInMembers);
  const products = useSelector(selectDineInProducts);
  const currentUserId = useSelector((state: RootState) => state.profile.data?.userId);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  if (!session) return null;

  const rows = buildDineInFeedRows(session, rounds, roundItems);

  const nameFor = (userId: string): string => {
    if (userId && userId === currentUserId) return "You";
    return members.find((m) => m.memberId === userId)?.memberName ?? "Someone";
  };
  const openerName = nameFor(session.creatorId);

  const activeSelectedItem = useMemo(() => {
    if (!selectedKey) return null;
    const row = rows.find((r) => r.type === "item" && r.key === selectedKey);
    if (!row || row.type !== "item") return null;
    const product = products.find((p) => p.productId === row.productId);
    const addedByName = nameFor(row.addedBy);
    const addedAt = formatTime(row.addedAt);
    return {
      product,
      variantId: row.variantId,
      quantity: row.quantity,
      addedByName,
      addedAt,
    };
  }, [rows, selectedKey, products, members, currentUserId]);

  return (
    <div className="dine-in-feed">
      {rows.map((row) => {
        if (row.type === "sessionOpened") {
          return (
            <p key="opened" className="dine-in-feed__system-row">
              {openerName} opened the table &middot; {formatTime(row.timestamp)}
            </p>
          );
        }
        if (row.type === "roundClosed") {
          const { itemCount, total } = roundOrderSummary(row.roundId, roundItems, products);
          return (
            <div key={`round-closed-${row.roundNumber}`}>
              <div className="dine-in-round-divider">
                <span className="dine-in-round-divider__pill">
                  <Check size={12} strokeWidth={2.5} />
                  Round {row.roundNumber} ordered &middot; {itemCount} item
                  {itemCount === 1 ? "" : "s"} &middot; ₹{Math.round(total)}
                </span>
              </div>
              <p className="dine-in-round-divider__time">{formatTime(row.timestamp)}</p>
            </div>
          );
        }
        if (row.type === "roundOpen") {
          return (
            <p key={`round-open-${row.roundNumber}`} className="dine-in-round-open">
              <span className="dine-in-round-open__dot" />
              Round {row.roundNumber} open &middot; add your next item
            </p>
          );
        }
        const product = products.find((p) => p.productId === row.productId);
        const addedByName = nameFor(row.addedBy);
        const addedAt = formatTime(row.addedAt);
        return (
          <DineInFeedItemCard
            key={row.key}
            product={product}
            quantity={row.quantity}
            variantId={row.variantId}
            addedByName={addedByName}
            addedAt={addedAt}
            isMine={!!currentUserId && row.addedBy === currentUserId}
            status={row.status}
            statusNote={row.statusNote}
            onOpenDetails={() =>
              setSelectedKey(row.key)
            }
          />
        );
      })}

      {activeSelectedItem && (
        <DineInProductDetailsModal
          product={activeSelectedItem.product}
          variantId={activeSelectedItem.variantId}
          quantity={activeSelectedItem.quantity}
          addedByName={activeSelectedItem.addedByName}
          addedAt={activeSelectedItem.addedAt}
          dineInId={session.sessionId}
          onClose={() => setSelectedKey(null)}
        />
      )}
    </div>
  );
};

export default DineInFeedList;
